import React, { PropsWithChildren, memo } from "react"
import { FieldValues, FormProvider, UseFormReturn } from "react-hook-form"
import cs from "classnames"

import type { Props as TextFieldProps } from "./TextField"
import type { Props as DatePickerProps } from "./DatePicker"
import type { Props as AutocompleteProps } from "./Autocomplete/Autocomplete"
import type { Props as CheckBoxProps } from "./CheckBox"
import type { Props as CheckBoxesProps } from "./CheckBoxes"
import type { Props as SelectProps } from "./Select"
import type { Props as TagsProps } from "./Tags"
import type { Props as LocationProps } from "./Locations"
import type { Props as RichTextFieldProps } from "./RichTextField"
import type { Props as RadiosProps } from "./Radios"
import type { Props as ButtonsSelectorProps } from "./ButtonsSelector"
import type { Props as SwitchProps } from "./Switch"
import type { Props as ColorPickerProps } from "./ColorPicker"
import type { Props as DateRangePickerProps } from "./DateRangePicker"
import type { Props as FilesProps } from "./Files"
import type { Props as PhoneInputProps } from "./PhoneInput"
import type { Props as SliderProps } from "./Slider"
import type { PhoneInputWithPhoneNumbersProps } from "./PhoneInputWithPhoneNumbers"

import { GenericFormComponent } from "./GenericFormComponent"
import MuiThemeProvider from "./MuiThemeProvider"

import { FormField } from "./types"

import styles from "./Forms.module.scss"

type WithCustomComponent<FormType, ComponentType> = {
  component: React.FC<FormField<FormType> & ComponentType>
}

type ConditionalProps<FormType, ComponentType> =
  | ({
      component: "TextField"
    } & TextFieldProps)
  | ({
      component: "DatePicker"
    } & DatePickerProps)
  | ({
      component: "CheckBox"
    } & CheckBoxProps)
  | ({
      component: "CheckBoxes"
    } & CheckBoxesProps)
  | ({
      component: "Autocomplete"
    } & AutocompleteProps)
  | ({
      component: "Select"
    } & SelectProps)
  | ({
      component: "Locations"
    } & LocationProps)
  | ({
      component: "Tags"
    } & TagsProps)
  | ({
      component: "RichTextField"
    } & RichTextFieldProps)
  | ({
      component: "Radios"
    } & RadiosProps)
  | ({
      component: "ButtonsSelector"
    } & ButtonsSelectorProps)
  | ({
      component: "Switch"
    } & SwitchProps)
  | ({
      component: "ColorPicker"
    } & ColorPickerProps)
  | ({
      component: "DateRangePicker"
    } & DateRangePickerProps)
  | ({
      component: "Files"
    } & FilesProps)
  | ({
      component: "PhoneInput"
    } & PhoneInputProps)
  | ({
      component: "PhoneInputWithPhoneNumbers"
    } & PhoneInputWithPhoneNumbersProps)
  | ({
      component: "Slider"
    } & SliderProps)
  | (WithCustomComponent<FormType, ComponentType> &
      FormField<FormType> &
      React.ComponentProps<WithCustomComponent<FormType, ComponentType>["component"]>)

export type GenericFormField<FormType, ComponentType = {}> = FormField<FormType> &
  ConditionalProps<FormType, ComponentType>

export type WrappedGenericFormFields<T> = {
  wrapper: React.FC<PropsWithChildren & { className?: string }>
  fields: (GenericFormField<T> | WrappedGenericFormFields<T> | React.ReactNode)[]
  hidden?: boolean
  loading?: boolean
}

export interface Props<T extends FieldValues> extends PropsWithChildren {
  className?: string
  fields?: (GenericFormField<T> | WrappedGenericFormFields<any>)[]
  formMethods: UseFormReturn<T>
  startAdornment?: React.ReactNode
  testId?: string

  onSubmit?: (form: T) => void
}

export const isWrappedFields = (
  field: GenericFormField<any> | WrappedGenericFormFields<any> | React.ReactNode
): field is WrappedGenericFormFields<any> => {
  return !!(field as WrappedGenericFormFields<any>).wrapper
}

export const isField = (
  field: GenericFormField<any> | WrappedGenericFormFields<any> | React.ReactNode
): field is GenericFormField<any> => {
  return !!(field as GenericFormField<any>).name
}

const _Form = <T extends FieldValues>({
  className,
  fields = [],
  children,
  onSubmit,
  formMethods,
  startAdornment,
  testId,
}: Props<T>) => {
  return (
    <MuiThemeProvider>
      <FormProvider {...formMethods}>
        <form
          className={cs(styles.root, className)}
          onSubmit={onSubmit && formMethods.handleSubmit(onSubmit)}
          data-testid={testId}
        >
          {startAdornment}
          {fields.map((field, i) => {
            return <PossiblyWrappedField field={field} key={`field-${i}`} />
          })}
          {children}
        </form>
      </FormProvider>
    </MuiThemeProvider>
  )
}

export const Form = memo(_Form) as typeof _Form

interface PossiblyWrappedFieldProps {
  field: GenericFormField<any> | WrappedGenericFormFields<any>
}

const PossiblyWrappedField = ({ field }: PossiblyWrappedFieldProps) => {
  if (isWrappedFields(field)) {
    const Wrapper = field.wrapper

    return (
      <Wrapper className={cs({ [styles.hidden]: field.hidden })}>
        {field.fields.map((field, i) => {
          if ((field && isField(field)) || isWrappedFields(field)) {
            return <PossiblyWrappedField field={field} key={`field-${i}`} />
          }

          return field
        })}
      </Wrapper>
    )
  }

  return <GenericFormComponent field={field} className={cs({ [styles.hidden]: field.hidden })} />
}
